import classNames from "classnames";
import React, { Component } from "react";
import { Manager, Popper, PopperChildrenProps, Reference, ReferenceChildrenProps } from "react-popper";
import { Overlay } from "../overlay/overlay";
import { ResizeObserver } from "../resize-observer/resize-observer";
import { POPOVER_CSS_CLASSES as CLASSES } from "./popover-css-classes";
import { PopoverContentHoverHandlers, PopoverProps, PopoverState } from "./popover.types";

const NOOP_FN = () => {};

const DEFAULT_ANCHOR_TAG_NAME = "span";

/**
 * The `Popover` component can be used to displays floating content that is positioned relative to anchor element
 * called the *anchor* element.
 *
 * The positioning implementation of `Popover` is built using the [react-popover](https://github.com/popperjs/react-popper)
 * library, which is the official React binding for the underlying [PopperJS](https://popper.js.org/docs/v2/) library
 * (`@popover/core`).
 *
 * The `Popover` component is used internally in other Blueprint components, like `Select` and `ContextMenu`.
 *
 * It can be used independently to creating a custom component with popover interaction.
 *
 * It internally uses the `Overlay`, and many of `Overlay` component props can be passed to it as well. These will be
 * relayed as is to the underlying `Overlay` component.
 *
 * Note that setting the `showBackdrop` to `true` is only supported when when the `triggerInteraction` is set to `click`.
 */
export class Popover extends Component<PopoverProps, PopoverState> {
  /**
   * Default props
   */
  static defaultProps = {
    placement: "auto",
    triggerInteraction: "click",
    openOnAnchorFocus: true,
    disabled: false,
    hoverOpenDelay: 150,
    hoverCloseDelay: 300,
    boundary: "clippingParents",
    defaultOpen: false,
    anchorTagName: DEFAULT_ANCHOR_TAG_NAME,
    anchorProps: {},
    showBackdrop: false,
  };

  /**
   * Timeout id for delayed state update.
   */
  timeoutID: number | null = null;

  /**
   * Timeout id for flushing event queue.
   */
  flushTimeoutId: number | null = null;

  /**
   * Ref of the anchor element.
   */
  anchorRef: HTMLElement | undefined;

  /**
   * Ref of the content element.
   */
  contentRef: HTMLElement | undefined;

  /**
   * A flag to refine focus handling
   */
  isNextFocusOnSamePage = true;

  /**
   * A flag that helps us handle the case when mouse moves across the gap between the anchor and the content
   */
  isMouseInAnchorOrContent = false;

  /**
   * Indicates if the popover is set up as controlled or uncontrolled.
   */
  isControlled = () => this.props.open !== undefined;

  /**
   * If `disabled` props is `true` don't open the popover.
   * Otherwise use the state indicated by `open` prop, using `false` if undefined.
   */
  getOpenState = () => {
    const { disabled, open, defaultOpen } = this.props;

    if (disabled) {
      return false;
    }

    return this.isControlled() ? open : this.state?.open === undefined ? defaultOpen : this.state.open;
  };

  /**
   *
   */
  setOpenState = (
    newOpen: boolean,
    e?: React.SyntheticEvent<HTMLElement>,
    timeout?: number,
    forceSetState: boolean = false
  ) => {
    this.timeoutID && window.clearTimeout(this.timeoutID);

    const isControlled = this.isControlled();

    if (timeout) {
      this.timeoutID = window.setTimeout(() => this.setOpenState(newOpen, e), timeout);
    } else {
      if (isControlled) {
        // relay the new `open` state out if in `uncontrolled` mode
        this.props.onInteraction && this.props.onInteraction(newOpen, e);
      }

      if (!isControlled || forceSetState) {
        // set internal state with the new `open` state
        this.setState({ open: newOpen });
      }

      // if popover closing, call the `Overlay` `onClose` handler if specified
      if (newOpen === false && this.props.onClose) {
        this.props.onClose(e as any);
      }
    }
  };

  /**
   * Initialize state based on props.
   * */
  state: PopoverState = {
    open: !!this.getOpenState(),
  };

  componentDidUpdate = () => {
    const newOpenState = !!this.getOpenState();

    if (newOpenState !== this.state.open) {
      this.setOpenState(newOpenState, undefined, undefined, true);
    }
  };

  componentWillUnmount = () => {
    this.timeoutID && window.clearTimeout(this.timeoutID);
    this.flushTimeoutId && window.clearTimeout(this.flushTimeoutId);
  };

  isTriggerInteractionHover = () =>
    this.props.triggerInteraction === "hover" || this.props.triggerInteraction === "hover-anchor-only";

  isElementInContent = (element: Element) => this.contentRef?.contains(element);

  handleMouseEnter = (event: React.SyntheticEvent<HTMLElement>) => {
    this.isMouseInAnchorOrContent = true;

    // if we're entering the popover, and the mode is set to be `hover-anchor-only`, we want to manually
    // trigger the mouse leave event, as hovering over the popover shouldn't count.
    if (this.isElementInContent(event.target as Element) && this.props.triggerInteraction === "hover-anchor-only") {
      this.handleMouseLeave(event);
    } else if (!this.props.disabled) {
      // only begin opening popover when it is enabled
      this.setOpenState(true, event, this.props.hoverOpenDelay);
    }
    this.props.anchorProps.onMouseEnter?.(event as any);
  };

  handleMouseLeave = (event: React.SyntheticEvent<HTMLElement>) => {
    this.isMouseInAnchorOrContent = false;

    // wait until the event queue is flushed, because we want to leave the
    // popover open if the mouse entered the popover immediately after
    // leaving the target (or vice versa).
    this.flushTimeoutId = window.setTimeout(() => {
      if (this.isMouseInAnchorOrContent) {
        return;
      }
      // user-configurable closing delay is helpful when moving mouse from target to popover
      this.setOpenState(false, event, this.props.hoverCloseDelay);
    });
    this.props.anchorProps.onMouseLeave?.(event as any);
  };

  handleAnchorFocus = (event: React.FocusEvent<HTMLElement>) => {
    if (this.props.openOnAnchorFocus) {
      if (event.relatedTarget == null && !this.isNextFocusOnSamePage) {
        /**
         * do nothing - the anchor had focus but lost it because the webpage itself lost focus
         * (e.g. due to switching tabs).
         */
        return;
      }
      this.handleMouseEnter(event);
    }
    this.props.anchorProps.onFocus?.(event);
  };

  handleAnchorBlur = (event: React.FocusEvent<HTMLElement>) => {
    if (this.props.openOnAnchorFocus) {
      // if the next element to receive focus is within the popover, we'll want to leave the
      // popover open. e.relatedTarget ought to tell us the next element to receive focus, but if the user just
      // clicked on an element which is not focusable (either by default or with a tabIndex attribute),
      // it won't be set. So, we filter those out here and assume that a click handler somewhere else will
      // close the popover if necessary.
      if (event.relatedTarget != null && !this.isElementInContent(event.relatedTarget as HTMLElement)) {
        this.handleMouseLeave(event);
      }
    }
    this.isNextFocusOnSamePage = event.relatedTarget != null;
    this.props.anchorProps.onBlur?.(event);
  };

  handleAnchorClick = (event: React.MouseEvent<HTMLElement>) => {
    // ensure click did not originate from within inline popover before closing
    if (!this.props.disabled && !this.isElementInContent(event.target as HTMLElement)) {
      if (!this.isControlled()) {
        this.setState((prevState) => {
          return { open: !prevState.open };
        });
      } else {
        this.setOpenState(!this.props.open, event);
      }
    }
    this.props.anchorProps.onClick?.(event);
  };

  renderAnchor = ({ ref }: ReferenceChildrenProps) => {
    const {
      children: anchor,
      anchorTagName,
      openOnAnchorFocus,
      anchorClassName,
      anchorProps: receivedAnchorProps,
    } = this.props;
    const { open } = this.state;
    const isTriggerInteractionHover = this.isTriggerInteractionHover();

    const anchorProps: React.HTMLProps<HTMLElement> = isTriggerInteractionHover
      ? {
          onBlur: this.handleAnchorBlur,
          onFocus: this.handleAnchorFocus,
          onMouseEnter: this.handleMouseEnter,
          onMouseLeave: this.handleMouseLeave,
        }
      : {
          onClick: this.handleAnchorClick,
        };

    anchorProps.className = classNames(
      CLASSES.POPOVER_ANCHOR,
      {
        [CLASSES.POPOVER_OPEN]: open,
      },
      anchorProps.className,
      anchorClassName
    );

    anchorProps.ref = ref;

    const anchorTabIndex = anchor.props.tabIndex;
    const tabIndex = anchorTabIndex === undefined && openOnAnchorFocus ? 0 : anchorTabIndex;
    const enhancedAnchor = React.cloneElement(anchor, { tabIndex });

    const anchorToRender = React.createElement(
      anchorTagName,
      {
        ...receivedAnchorProps,
        ...anchorProps,
      },
      enhancedAnchor
    );

    return <ResizeObserver onResize={() => this.updatePopper()}>{anchorToRender}</ResizeObserver>;
  };

  renderContent = ({ ref, style, placement, arrowProps, update }: PopperChildrenProps) => {
    const { content, usePortal, triggerInteraction, contentClassName: contentClassNameProp } = this.props;

    // Need to update our reference to this on every render as it will change.
    this.updatePopper = update;

    const hoverHandlers: PopoverContentHoverHandlers = {};
    if (triggerInteraction === "hover" || (!usePortal && triggerInteraction === "hover-anchor-only")) {
      hoverHandlers.onMouseEnter = this.handleMouseEnter;
      hoverHandlers.onMouseLeave = this.handleMouseLeave;
    }

    const contentClassName = classNames(CLASSES.POPOVER_CONTENT, contentClassNameProp);

    const close = () => {
      this.setOpenState(false);
    };

    return (
      <ResizeObserver onResize={() => this.updatePopper()}>
        {typeof content === "function"
          ? content({
              innerRef: ref,
              anchorElement: this.anchorRef,
              style,
              placement,
              arrowProps,
              hoverHandlers,
              close,
              className: contentClassName,
            })
          : React.cloneElement(content, {
              ref,
              style,
              ...hoverHandlers,
              close: typeof content.type === "string" ? undefined : close,
              className: classNames(contentClassName, content.props.className),
            })}
      </ResizeObserver>
    );
  };

  updatePopper = NOOP_FN;

  /**
   * This handler is invoked when the `Overlay` is closing.
   *
   * If the Overlay is closing due to a mouse click event, and the event target happens to be the anchor element,
   * then the anchor event handle will handle updating our internal `open` state. So we don't need to do it here.
   *
   * If that's not the case, or, the `Overlay` is closing due to a KeyEvent (`Esc` key) then we do need to
   * update our internal `open` state to `false`.
   */
  handleOverlayClose = (event: React.SyntheticEvent<HTMLElement>) => {
    const eventTarget = event.target as HTMLElement;
    const anchorElement = this.anchorRef;

    if (
      (anchorElement && eventTarget && anchorElement !== eventTarget && !anchorElement.contains(eventTarget)) ||
      event.nativeEvent instanceof KeyboardEvent
    ) {
      this.setOpenState(false, event);
    }
  };

  render = () => {
    const { placement, className, overlayRootProps, modifiers, showBackdrop, backdropClassName } = this.props;
    const { open } = this.state;

    const rootClassName = classNames(CLASSES.POPOVER_ROOT, className);

    return (
      <Manager>
        <div className={rootClassName}>
          <Reference
            innerRef={(el) => {
              this.anchorRef = el || this.anchorRef;
            }}
          >
            {this.renderAnchor}
          </Reference>
          <Overlay
            open={open}
            onClose={this.handleOverlayClose}
            closeOnOutsideClick={this.props.triggerInteraction === "click"}
            closeOnEscapeKey={this.props.closeOnEscapeKey}
            usePortal={this.props.usePortal}
            lazyPortal={this.props.lazyPortal}
            preserveDomElement={this.props.preserveDomElement}
            portalChildrenDidMount={this.props.portalChildrenDidMount}
            portalContainer={this.props.portalContainer}
            portalClassName={this.props.portalClassName}
            showBackdrop={showBackdrop}
            backdropClassName={backdropClassName}
            className={this.props.overlayClassName}
            rootProps={overlayRootProps}
          >
            <Popper
              innerRef={(el) => {
                this.contentRef = el || this.contentRef;
              }}
              placement={placement}
              modifiers={modifiers}
            >
              {this.renderContent}
            </Popper>
          </Overlay>
        </div>
      </Manager>
    );
  };
}
